import {
  createEventResult as eventResult,
  createDocument as document,
  createMessage as message,
  createCoverage as coverage
} from "../../../helpers/backend_helper";
import {
  apiError,
  siscotelSuccess,
  reset_siscotel_flag,
} from "./reducer";

// Función para serializar el error antes de pasarlo a Redux
// Extrae el mensaje de Siscotel (answer) si está disponible en la respuesta HTTP
const serializeError = (error) => {
  // Si hay respuesta HTTP con answer de Siscotel, usarla
  if (error.response?.data?.answer) {
    return error.response.data.answer;
  }
  // Si hay respuesta HTTP con message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  return error.message || "Error desconocido";
};

/**
 * Valida la respuesta de Siscotel.
 * Siscotel puede responder HTTP 200 pero con status de error en el body.
 * Ejemplo: {"status":400,"answer":"Factura invalida"}
 * 
 * @param {Object} response - Respuesta del API
 * @returns {Object} - { isSuccess, siscotelStatus, siscotelAnswer }
 */
const validateSiscotelResponse = (response) => {
  // Extraer el status de Siscotel del body (puede estar en diferentes ubicaciones)
  const siscotelStatus = 
    response?.data?.status ||
    response?.payload?.data?.status ||
    response?.payload?.status;
  
  const siscotelAnswer = 
    response?.data?.answer || 
    response?.payload?.data?.answer || 
    response?.payload?.answer ||
    "Error desconocido";

  // HTTP exitoso Y status de Siscotel = 200
  const httpSuccess = response?.status === 200;
  const siscotelSuccess = siscotelStatus === 200;
  const isSuccess = httpSuccess && siscotelSuccess;

  return {
    isSuccess,
    siscotelStatus,
    siscotelAnswer,
    httpSuccess
  };
};

/**
 * Asynchronous thunk action to create an event result for Siscotel.
 *
 * @param {Object} siscotel - The Siscotel object containing event result data.
 * @returns {Function} A thunk function that dispatches actions based on the API response.
 */
export const createEventResult = (siscotel) => async (dispatch) => {
  const maxRetries = 2;
  const retryDelay = 2000; // 2 segundos
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🚀 createEventResult - Intento ${attempt + 1}/${maxRetries + 1}`);
      const response = await eventResult(siscotel);
      console.log('📥 Respuesta de eventResult:', response);
      
      // Validar respuesta de Siscotel (HTTP 200 no garantiza éxito)
      const validation = validateSiscotelResponse(response);
      console.log('✅ Validación:', validation);
      
      // Crear un objeto serializable para Redux
      const serializableData = {
        data: response.data,
        message: response.message,
        status: response.status,
        siscotelStatus: validation.siscotelStatus,
        siscotelAnswer: validation.siscotelAnswer,
        isSuccess: validation.isSuccess
      };

      // Si Siscotel respondió con error en el body, lanzar excepción
      if (!validation.isSuccess) {
        const error = new Error(validation.siscotelAnswer);
        error.siscotelError = true;
        error.siscotelStatus = validation.siscotelStatus;
        throw error;
      }

      dispatch(siscotelSuccess(serializableData));
      return serializableData;
    } catch (error) {
      // Si es error de Siscotel, no reintentar
      if (error.siscotelError) {
        throw error;
      }
      
      // Si es un error HTTP 400 con answer, tratarlo como error
      if (error.response?.status === 400 && error.response?.data?.answer) {
        const siscotelAnswer = error.response.data.answer;
        const siscotelError = new Error(siscotelAnswer);
        siscotelError.siscotelError = true;
        siscotelError.siscotelStatus = error.response.data.status;
        throw siscotelError;
      }
      
      // Si es el último intento o no es un error 500, lanzar error
      if (attempt === maxRetries || (error.response && error.response.status !== 500)) {
        dispatch(apiError(serializeError(error)));
        throw error;
      }
      
      // Esperar antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }
};




/**
 * Asynchronous thunk action to create a document for siscotel.
 *
 * @param {Object} siscotel - The siscotel object to create a document for.
 * @returns {Function} A thunk function that dispatches actions based on the API response.
 */
export const createDocument = (siscotel) => async (dispatch) => {
  const maxRetries = 2;
  const retryDelay = 2000; // 2 segundos
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🚀 createDocument - Intento ${attempt + 1}/${maxRetries + 1}`);
      const response = await document(siscotel);
      console.log('📥 Respuesta de document:', response);
      
      // Validar respuesta de Siscotel (HTTP 200 no garantiza éxito)
      const validation = validateSiscotelResponse(response);
      console.log('✅ Validación:', validation);
      
      // Crear un objeto serializable para Redux
      const serializableData = {
        data: response.data,
        message: response.message,
        status: response.status,
        siscotelStatus: validation.siscotelStatus,
        siscotelAnswer: validation.siscotelAnswer,
        isSuccess: validation.isSuccess
      };

      // Si Siscotel respondió con error en el body, lanzar excepción
      if (!validation.isSuccess) {
        const error = new Error(validation.siscotelAnswer);
        error.siscotelError = true;
        error.siscotelStatus = validation.siscotelStatus;
        throw error;
      }

      dispatch(siscotelSuccess(serializableData));
      return serializableData;
    } catch (error) {
      // Si es error de Siscotel, no reintentar
      if (error.siscotelError) {
        throw error;
      }
      
      // Si es un error HTTP 400 con answer, tratarlo como error
      if (error.response?.status === 400 && error.response?.data?.answer) {
        const siscotelAnswer = error.response.data.answer;
        const siscotelError = new Error(siscotelAnswer);
        siscotelError.siscotelError = true;
        siscotelError.siscotelStatus = error.response.data.status;
        throw siscotelError;
      }
      
      // Si es el último intento o no es un error 500, lanzar error
      if (attempt === maxRetries || (error.response && error.response.status !== 500)) {
        dispatch(apiError(serializeError(error)));
        throw error;
      }
      
      // Esperar antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }
};



/**
 * Asynchronous thunk action to create a message for Siscotel.
 *
 * @param {Object} siscotel - The Siscotel object.
 * @returns {Function} A thunk function that dispatches actions based on the API response.
 */
export const createMessage = (siscotel) => async (dispatch) => {
  const maxRetries = 2;
  const retryDelay = 2000; // 2 segundos
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🚀 createMessage - Intento ${attempt + 1}/${maxRetries + 1}`);
      const response = await message(siscotel);
      console.log('📥 Respuesta de message:', response);
      
      // Validar respuesta de Siscotel (HTTP 200 no garantiza éxito)
      const validation = validateSiscotelResponse(response);
      console.log('✅ Validación:', validation);
      
      // Crear un objeto serializable para Redux
      const serializableData = {
        data: response.data,
        message: response.message,
        status: response.status,
        siscotelStatus: validation.siscotelStatus,
        siscotelAnswer: validation.siscotelAnswer,
        isSuccess: validation.isSuccess
      };

      // Si Siscotel respondió con error en el body, lanzar excepción
      if (!validation.isSuccess) {
        const error = new Error(validation.siscotelAnswer);
        error.siscotelError = true;
        error.siscotelStatus = validation.siscotelStatus;
        throw error;
      }

      dispatch(siscotelSuccess(serializableData));
      return serializableData;
    } catch (error) {
      // Si es error de Siscotel, no reintentar
      if (error.siscotelError) {
        throw error;
      }
      
      // Si es un error HTTP 400 con answer, tratarlo como error
      if (error.response?.status === 400 && error.response?.data?.answer) {
        const siscotelAnswer = error.response.data.answer;
        const siscotelError = new Error(siscotelAnswer);
        siscotelError.siscotelError = true;
        siscotelError.siscotelStatus = error.response.data.status;
        throw siscotelError;
      }
      
      // Si es el último intento o no es un error 500, lanzar error
      if (attempt === maxRetries || (error.response && error.response.status !== 500)) {
        dispatch(apiError(serializeError(error)));
        throw error;
      }
      
      // Esperar antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }
};




/**
 * Asynchronous thunk action to create coverage.
 *
 * @param {Object} siscotel - The siscotel object to be used in the action.
 * @returns {Function} A thunk function that dispatches actions based on the API response.
 */
export const createCoverage = (siscotel) => async (dispatch) => {
  const maxRetries = 2;
  const retryDelay = 2000; // 2 segundos
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🚀 Intento ${attempt + 1}/${maxRetries + 1} - Llamando a coverage()...`);
      const response = await coverage(siscotel);
      console.log('📥 Respuesta de coverage:', response);
      
      // Validar respuesta de Siscotel (HTTP 200 no garantiza éxito)
      const validation = validateSiscotelResponse(response);
      console.log('✅ Validación de Siscotel:', validation);
      
      // Crear un objeto serializable para Redux
      const serializableData = {
        data: response.data,
        message: response.message,
        status: response.status,
        siscotelStatus: validation.siscotelStatus,
        siscotelAnswer: validation.siscotelAnswer,
        isSuccess: validation.isSuccess
      };

      // Si Siscotel respondió con error en el body, lanzar excepción
      if (!validation.isSuccess) {
        const error = new Error(validation.siscotelAnswer);
        error.siscotelError = true;
        error.siscotelStatus = validation.siscotelStatus;
        throw error;
      }

      dispatch(siscotelSuccess(serializableData));
      return serializableData;
    } catch (error) {
      // Si es error de Siscotel, no reintentar
      if (error.siscotelError) {
        throw error;
      }
      
      // Si es un error HTTP 400 con answer, tratarlo como error
      if (error.response?.status === 400 && error.response?.data?.answer) {
        const siscotelAnswer = error.response.data.answer;
        const siscotelError = new Error(siscotelAnswer);
        siscotelError.siscotelError = true;
        siscotelError.siscotelStatus = error.response.data.status;
        throw siscotelError;
      }
      
      // Si es el último intento o no es un error 500, lanzar error
      if (attempt === maxRetries || (error.response && error.response.status !== 500)) {
        dispatch(apiError(serializeError(error)));
        throw error;
      }
      
      // Esperar antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
    }
  }
};

/**
 * Asynchronous thunk action to reset the siscotel flag.
 *
 * This function dispatches the `reset_siscotel_flag` action and returns the response.
 *
 * @returns {Promise} The response from the `reset_siscotel_flag` action.
 */
export const resetSiscotelFlag = () => async (dispatch) => {
  try {
    const response = dispatch(reset_siscotel_flag());
    return response;
  } catch (error) {
    dispatch(apiError(serializeError(error)));
    throw error;
  }
};